import { useEffect, useState } from "react";
import api, { fmtErr } from "@/lib/api";

/**
 * BadgeAccessories — manages the user's owned badges (max 3 displayed).
 * Designed as a dropdown so it slots inside the AccountPage Profile tab without taking over.
 */
export default function BadgeAccessories() {
  const [data, setData] = useState({ owned: [], displayed: [], catalog: {}, max_displayed: 3 });
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  useEffect(() => { reload(); }, []);
  async function reload() {
    try { const r = await api.get("/badges/me"); setData(r.data); }
    catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); }
  }
  async function toggle(bid) {
    const isOn = data.displayed.includes(bid);
    let next = isOn ? data.displayed.filter(b => b !== bid) : [...data.displayed, bid];
    if (next.length > data.max_displayed) {
      setErr(`You can only display ${data.max_displayed} badges. Remove one first.`);
      setTimeout(() => setErr(""), 2500);
      return;
    }
    try {
      const { data: r } = await api.put("/badges/displayed", { displayed: next });
      setData(d => ({ ...d, displayed: r.displayed }));
      setMsg("Badges updated"); setTimeout(() => setMsg(""), 1500);
    } catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); }
  }

  return (
    <div data-testid="badge-accessories" className="bg-slate-900/40 border border-slate-800 rounded-xl">
      <button onClick={() => setOpen(o => !o)}
        className="w-full px-4 py-3 flex items-center justify-between text-xs uppercase font-bold text-slate-300 hover:bg-slate-800/40 transition-colors">
        <span><i className="fa-solid fa-medal text-amber-400 mr-2"/>Accessories — Badges ({data.displayed.length}/{data.max_displayed} displayed, {data.owned.length} owned)</span>
        <i className={`fa-solid fa-chevron-${open ? "up" : "down"} text-slate-500`}/>
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-slate-800 pt-3">
          {msg && <div className="text-[10px] text-emerald-300 mb-2">{msg}</div>}
          {err && <div className="text-[10px] text-rose-300 mb-2">{err}</div>}
          {data.owned.length === 0 ? (
            <div className="text-xs text-slate-500 py-2">No badges yet. Earn one by reaching Elite tier, becoming an admin, or buying via Store.</div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {data.owned.map(bid => {
                const c = data.catalog[bid] || { name: bid };
                const isOn = data.displayed.includes(bid);
                return (
                  <button key={bid} data-testid={`badge-toggle-${bid}`} onClick={() => toggle(bid)}
                    className={`relative aspect-square rounded-lg border-2 p-2 flex flex-col items-center justify-center transition-all ${isOn ? "border-amber-500 bg-amber-500/10" : "border-slate-800 bg-slate-900/40 hover:border-slate-600"}`}>
                    {c.image_url
                      ? <img src={c.image_url} alt={c.name} className="w-10 h-10 object-contain"/>
                      : <div className="w-10 h-10 rounded bg-slate-800 flex items-center justify-center text-lg font-black text-amber-300">{c.name?.slice(0,1) || "?"}</div>}
                    <div className="text-[9px] mt-1 text-slate-300 truncate w-full text-center">{c.name}</div>
                    {isOn && <span className="absolute top-1 right-1 text-[9px] text-amber-300"><i className="fa-solid fa-eye"/></span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
